// CT welkomst-pop-up — hoort bij sections/ct-welkom-popup.liquid
// Hergebruikt theme.Modals (assets/modal.js) voor open/dicht/focus en js-cookie via de importmap.
// Opmaak is eigen full-screen (assets/ct-welkom-popup.css), niet de .modal-box van het thema.
// v2 (3-11-2026): verzendroute robuuster (tags direct bij keuze, geen dubbele verzending),
// zware hCaptcha-logo-zoeker verwijderd (hCaptcha-tekst verschijnt automatisch als Shopify's
// spambeveiliging actief is), lichtere auto-apply, beelden vooraf laden bij openen.

import Cookies from 'js-cookie';
import '@archetype-themes/scripts/config';
import '@archetype-themes/scripts/modules/modal';

const COOKIE_CLOSED = 'ct_welkom_closed';
const COOKIE_DONE = 'ct_welkom_done';
const PAGEVIEW_KEY = 'ct_welkom_pageviews';
const FORM_HASH = '#ct-welkom-form';

const log = (...args) => console.info('[ct-welkom]', ...args);

// Is Shopify's spambeveiliging (hCaptcha) actief op deze pagina?
const captchaActive = () =>
  !!(window.Shopify && window.Shopify.captcha) || !!document.querySelector('script[src*="hcaptcha"]');

class CtWelkomPopup extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    const d = this.dataset;
    this.sectionId = d.sectionId;
    this.code = (d.code || '').trim();
    this.designMode = d.designMode === 'true';
    // Full screen: geen "klik buiten de inhoud"-sluiting (er is geen buiten)
    this.modal = new theme.Modals(this.id, 'ct-welkom-' + this.sectionId, { closeOffContentClick: false });

    this.form = this.querySelector('form');
    this.errorEl = this.querySelector('[data-error]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.tagsInput = this.querySelector('[data-tags-input]');

    this.querySelectorAll('[data-code-value]').forEach((el) => (el.textContent = this.code));
    this.querySelector('[data-copy]')?.addEventListener('click', () => this.copyCode());
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    // Tags direct bijwerken bij elke doelkeuze, zodat ze ook meegaan als de captcha de verzending overneemt
    this.form?.addEventListener('change', (e) => {
      if (e.target && e.target.name === 'ct_goal') this.syncTags();
    });
    this.syncTags();
    this.installSubmitHook();
    this.addHoneypot();
    document.addEventListener('modalClose.' + this.id, () => this.onClose());

    if (this.designMode) {
      this.classList.add('is-dismiss-ready');
      this.showCaptchaText(true);
      this.initEditor();
      return;
    }

    // Terugkomst na niet-JS/captcha-route: ?customer_posted=true#ct-welkom-form
    if (window.location.hash === FORM_HASH && /customer_posted=true/.test(window.location.search)) {
      this.prepareMedia();
      this.showSuccess();
      this.modal.open();
      this.classList.add('is-dismiss-ready');
      return;
    }

    if (Cookies.get(COOKIE_DONE) || Cookies.get(COOKIE_CLOSED)) return;
    if (['/challenge', '/password'].includes(window.location.pathname)) return;

    let views = 0;
    try {
      views = parseInt(sessionStorage.getItem(PAGEVIEW_KEY) || '0', 10) + 1;
      sessionStorage.setItem(PAGEVIEW_KEY, String(views));
    } catch (e) {
      views = 1;
    }
    if (views < parseInt(d.minPageviews || '1', 10)) return;

    this.scheduleOpen();
  }

  scheduleOpen() {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const seconds = parseInt(isMobile ? this.dataset.delayMobile : this.dataset.delayDesktop, 10) || 20;

    this.timer = window.setTimeout(() => this.tryOpen(), seconds * 1000);
    // Logo en foto's 2 sec vóór openen alvast laden, zodat ze er direct staan
    this.preloadTimer = window.setTimeout(() => this.prepareMedia(), Math.max(0, seconds - 2) * 1000);

    if (!isMobile && this.dataset.exitIntent === 'true') {
      // Pas na 5 sec activeren, zodat een snelle muisbeweging bij binnenkomst niet telt
      window.setTimeout(() => {
        this.exitHandler = (e) => {
          if (!e.relatedTarget && e.clientY <= 0) this.tryOpen();
        };
        document.documentElement.addEventListener('mouseout', this.exitHandler);
      }, 5000);
    }
  }

  // Lazy beelden in de pop-up omzetten naar direct laden
  prepareMedia() {
    if (this._mediaReady) return;
    this._mediaReady = true;
    this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  tryOpen() {
    if (this.opened) return;
    const html = document.documentElement;
    // Niet over een open drawer/modal heen; probeer het later nog eens
    if (html.classList.contains('modal-open') || html.classList.contains('js-drawer-open')) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.tryOpen(), 10000);
      return;
    }
    this.opened = true;
    window.clearTimeout(this.timer);
    window.clearTimeout(this.preloadTimer);
    if (this.exitHandler) document.documentElement.removeEventListener('mouseout', this.exitHandler);
    this.prepareMedia();
    this.showCaptchaText();
    this.formOpenedAt = Date.now();
    this.modal.open();
    this.revealDismiss();
  }

  // hCaptcha-tekst tonen als de instelling aan staat of als Shopify's spambeveiliging actief is.
  // Dan mag het zwevende hCaptcha-logo weg (via de class op <html>, zie CSS).
  showCaptchaText(force) {
    const el = this.querySelector('[data-captcha-text]');
    if (!el) return;
    if (force || this.dataset.captchaText === 'true' || captchaActive()) {
      el.hidden = false;
      document.documentElement.classList.add('ct-welkom-captcha-text');
    }
  }

  // Kruisje en 'Nee bedankt' pas na X seconden tonen (instelling), in de editor direct
  revealDismiss() {
    window.clearTimeout(this.dismissTimer);
    this.classList.remove('is-dismiss-ready');
    const seconds = parseFloat(this.dataset.dismissDelay || '0');
    if (this.designMode || !(seconds > 0)) {
      this.classList.add('is-dismiss-ready');
      return;
    }
    this.dismissTimer = window.setTimeout(() => this.classList.add('is-dismiss-ready'), seconds * 1000);
  }

  onClose() {
    if (this.designMode) return;
    if (!Cookies.get(COOKIE_DONE)) {
      Cookies.set(COOKIE_CLOSED, '1', { path: '/', expires: parseInt(this.dataset.daysAfterClose || '14', 10) });
    }
  }

  // Spamval: onzichtbaar veld dat alleen bots invullen, plus een minimale invultijd na openen.
  addHoneypot() {
    if (!this.form || this.form.querySelector('[name="ct_hp"]')) return;
    const wrap = document.createElement('div');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.cssText = 'position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;';
    wrap.innerHTML = '<label>Laat dit veld leeg <input type="text" name="ct_hp" tabindex="-1" autocomplete="off"></label>';
    this.form.appendChild(wrap);
    this.formOpenedAt = Date.now();
  }

  isLikelyBot() {
    const hp = this.form.querySelector('[name="ct_hp"]');
    if (hp && hp.value) return true;
    // Binnen 1,5 sec na openen invullen en versturen is geen mens
    return this.formOpenedAt && Date.now() - this.formOpenedAt < 1500;
  }

  setError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg || '';
    this.errorEl.hidden = !msg;
  }

  collectTags() {
    const base = (this.dataset.baseTags || '').split(',').map((t) => t.trim()).filter(Boolean);
    const goal = this.form?.querySelector('input[name="ct_goal"]:checked');
    if (goal && goal.value) base.push(goal.value.trim());
    return [...new Set(base)].join(',');
  }

  syncTags() {
    if (this.tagsInput && this.form) this.tagsInput.value = this.collectTags();
  }

  onSubmit(e) {
    const email = this.form.querySelector('input[type="email"]');
    if (!email.value || !email.checkValidity()) {
      e.preventDefault();
      this.setError(this.dataset.errorInvalid);
      email.focus();
      return;
    }
    this.setError('');
    this.syncTags();

    if (this.designMode) {
      e.preventDefault();
      this.showSuccess();
      return;
    }

    if (this.isLikelyBot()) {
      e.preventDefault();
      log('aanmelding tegengehouden (spamval)');
      this.setError(this.dataset.errorGeneric);
      return;
    }

    const hasToken = !!this.form.querySelector('[name="h-captcha-response"]')?.value;

    if (hasToken || !captchaActive()) {
      // Token al aanwezig (of captcha staat uit): zelf versturen zonder herladen
      e.preventDefault();
      this.ajaxSubmit();
      return;
    }

    // Captcha actief, nog geen token: geen preventDefault. Shopify's hCaptcha vangt de submit af,
    // voegt het token toe en verstuurt opnieuw; die verzending vangen we op (installSubmitHook).
    // Neemt Shopify het niet over, dan wordt het een gewone verzending (pagina toont daarna de code).
    this.submitBtn?.classList.add('btn--loading');
    window.clearTimeout(this.busyTimer);
    this.busyTimer = window.setTimeout(() => {
      if (!this.sending) this.submitBtn?.classList.remove('btn--loading');
    }, 15000);
  }

  // Shopify's captcha verstuurt na het toevoegen van het token met form.submit() of
  // form.requestSubmit(). requestSubmit komt weer in onSubmit (met token); form.submit()
  // vangen we hier af, alleen voor dit formulier.
  installSubmitHook() {
    if (window.__ctWelkomSubmitHook) return;
    window.__ctWelkomSubmitHook = true;
    const nativeSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function () {
      const host = this.closest && this.closest('ct-welkom-popup');
      if (host && this.id === 'ct-welkom-form' && !this.dataset.ctNative && typeof host.ajaxSubmit === 'function') {
        host.ajaxSubmit();
        return;
      }
      return nativeSubmit.call(this);
    };
  }

  nativeSubmit() {
    this.form.dataset.ctNative = '1';
    HTMLFormElement.prototype.submit.call(this.form);
  }

  setBusy(busy) {
    if (!this.submitBtn) return;
    this.submitBtn.classList.toggle('btn--loading', busy);
    if (busy) this.submitBtn.setAttribute('aria-busy', 'true');
    else this.submitBtn.removeAttribute('aria-busy');
    this.submitBtn.disabled = busy;
  }

  async ajaxSubmit() {
    if (this.sending || this.done) return;
    // Ook via de captcha-route: tags bijwerken en spamval checken
    this.syncTags();
    if (this.isLikelyBot()) {
      log('aanmelding tegengehouden (spamval)');
      this.setError(this.dataset.errorGeneric);
      this.submitBtn?.classList.remove('btn--loading');
      return;
    }

    this.sending = true;
    this.setBusy(true);
    let goNative = false;
    try {
      const data = new URLSearchParams(new FormData(this.form));
      data.delete('ct_hp');
      const res = await fetch(this.form.action, {
        method: 'POST',
        body: data, // urlencoded, net als een gewone formulierverzending
        headers: { Accept: 'text/html' },
        credentials: 'same-origin'
      });
      const url = res.url || '';
      log('antwoord', res.status, url);

      if (url.includes('/challenge')) {
        // Interactieve captcha nodig: enige situatie waarin we de gewone verzending gebruiken
        goNative = true;
        return;
      }

      if (url.includes('customer_posted=true')) {
        this.showSuccess();
        return;
      }

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const err = doc.querySelector('#ct-welkom-form .errors');
      if (err && err.textContent.trim()) {
        this.setError(err.textContent.trim().replace(/\s+/g, ' '));
        return;
      }

      if (res.ok) {
        // Aanvraag geaccepteerd zonder foutmelding: geen tweede verzending, gewoon de code tonen
        log('geen customer_posted in antwoord, behandeld als geslaagd');
        this.showSuccess();
      } else {
        this.setError(this.dataset.errorGeneric);
      }
    } catch (error) {
      console.warn('[ct-welkom] verzenden mislukt', error);
      this.setError(this.dataset.errorGeneric);
    } finally {
      this.sending = false;
      this.setBusy(false);
      if (goNative) this.nativeSubmit();
    }
  }

  showSuccess() {
    this.done = true;
    this.querySelector('[data-step="form"]').hidden = true;
    const success = this.querySelector('[data-step="success"]');
    success.hidden = false;
    // Dialoog krijgt de succestitel als naam
    const title = success.querySelector('[data-success-title]');
    if (title && title.id) this.querySelector('[role="dialog"]')?.setAttribute('aria-labelledby', title.id);

    if (this.designMode) return;

    Cookies.set(COOKIE_DONE, '1', { path: '/', expires: 365 });
    Cookies.remove(COOKIE_CLOSED, { path: '/' });

    // Nette URL na de captcha-route
    if (window.location.hash === FORM_HASH) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('customer_posted');
      clean.hash = '';
      window.history.replaceState({}, '', clean.toString());
    }

    if (this.dataset.autoApply === 'true' && this.code) {
      // Code als kortingscookie zetten; redirect naar het lichte /cart.js i.p.v. de hele homepage
      fetch('/discount/' + encodeURIComponent(this.code) + '?redirect=%2Fcart.js', { credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error('status ' + res.status);
          const applied = this.querySelector('[data-applied]');
          if (applied) applied.hidden = false;
        })
        .catch((err) => log('code automatisch toepassen mislukt', err));
    }

    document.dispatchEvent(new CustomEvent('ct-welkom:subscribed', { detail: { code: this.code } }));
    window.setTimeout(() => success.querySelector('[data-copy]')?.focus(), 150);
  }

  async copyCode() {
    const btn = this.querySelector('[data-copy]');
    try {
      await navigator.clipboard.writeText(this.code);
    } catch (e) {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-code-value]'));
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      try { document.execCommand('copy'); } catch (err) {}
    }
    if (btn) {
      if (!btn.dataset.labelDefault) btn.dataset.labelDefault = btn.textContent.trim();
      btn.textContent = btn.dataset.labelDone;
      window.setTimeout(() => (btn.textContent = btn.dataset.labelDefault), 2500);
    }
  }

  initEditor() {
    const showState = () => {
      const success = this.dataset.previewState === 'success';
      this.querySelector('[data-step="form"]').hidden = success;
      this.querySelector('[data-step="success"]').hidden = !success;
    };
    showState();

    const isMine = (e) => e.detail && e.detail.sectionId === this.sectionId;
    document.addEventListener('shopify:section:select', (e) => (isMine(e) ? this.modal.open() : this.modal.close()));
    document.addEventListener('shopify:section:deselect', (e) => isMine(e) && this.modal.close());
    document.addEventListener('shopify:block:select', (e) => {
      if (!isMine(e)) return;
      this.querySelector('[data-step="form"]').hidden = false;
      this.querySelector('[data-step="success"]').hidden = true;
      this.modal.open();
    });
  }
}

if (!customElements.get('ct-welkom-popup')) {
  customElements.define('ct-welkom-popup', CtWelkomPopup);
}
